import type { SettingsTenant } from "@/types";

/**
 * The clinic's timezone as Laravel holds it (SCHED-TZ-01). The web never picks
 * one: not the browser's zone, not a São Paulo default. A clinic without a
 * timezone is shown as such, and only the admin can set it.
 */
export const CLINIC_TIMEZONES = [
  { value: "America/Sao_Paulo", label: "Brasília (São Paulo)" },
  { value: "America/Manaus", label: "Amazonas (Manaus)" },
  { value: "America/Cuiaba", label: "Mato Grosso (Cuiabá)" },
  { value: "America/Rio_Branco", label: "Acre (Rio Branco)" },
  { value: "America/Noronha", label: "Fernando de Noronha" },
] as const;

export function clinicTimezoneLabel(timezone: string): string {
  return CLINIC_TIMEZONES.find((option) => option.value === timezone)?.label ?? timezone;
}

/** What the admin still owes about the clinic timezone. */
export type ClinicTimezoneSetup = "missing" | "unconfirmed" | null;

/**
 * `missing`: no timezone on the server. `unconfirmed`: a timezone the clinic
 * never saved itself (the OQ-48 backfill), reported as `timezone_confirmed_at:
 * null`. An absent `timezone_confirmed_at` raises nothing: saving could not
 * clear an item the server does not track. That absent-key case is a
 * compatibility branch, owner SCHED-TZ-01; remove after the api half deploys
 * (key always present).
 */
export function clinicTimezoneSetup(
  settings: Pick<SettingsTenant, "timezone" | "timezone_confirmed_at"> | undefined
): ClinicTimezoneSetup {
  if (!settings) return null;
  if (!settings.timezone) return "missing";
  return settings.timezone_confirmed_at === null ? "unconfirmed" : null;
}

const DISMISS_KEY_PREFIX = "clinisharp.timezone-notice-dismissed.";

/**
 * The dashboard notice is dismissed per clinic for the browser session, like
 * the realtime notice: the gap it describes is still open, so the next session
 * shows it again until the admin saves the timezone. A blocked store reads as
 * "not dismissed".
 */
export function timezoneNoticeDismissed(tenantId: string | null | undefined): boolean {
  if (!tenantId) return false;
  try {
    return window.sessionStorage.getItem(DISMISS_KEY_PREFIX + tenantId) === "1";
  } catch {
    return false;
  }
}

export function dismissTimezoneNotice(tenantId: string | null | undefined): void {
  if (!tenantId) return;
  try {
    window.sessionStorage.setItem(DISMISS_KEY_PREFIX + tenantId, "1");
  } catch {
    // Ignored: the caller hides the notice from component state anyway.
  }
}
